import type { FC } from "react";
import { useMemo } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { getDeliveryLimitCitiesDropdownListV2 } from "@ui/utils/getDeliveryLimitCitiesDropdownListV2/getDeliveryLimitCitiesDropdownListV2";

import type { LocationServiceAutocompleteService, RegionWithRequiredFields } from "@jti/fe-common";
import { getRegionOptions } from "@jti/fe-common";
import type { IGetOrderDeliveryLimits, PostCodeConfig } from "@jti/magento";

import { CityField, CountryField } from "../AddressFormProvider";
import { useFormContext } from "../Form";
import { PostCode } from "../PostCode";
import { RegionField } from "../RegionField";
import { StreetAddressFields } from "../StreetAddressFields";

import styles from "./StandardAddressForm.module.scss";

export type StandardAddressFormProps = {
  fieldName?(name: string): string;
  enableStreetNameAutocomplete?: boolean;
  orderDeliveryLimits?: IGetOrderDeliveryLimits;
  postCodeConfig?: PostCodeConfig;
  locationServiceAutocompleteProvider?: LocationServiceAutocompleteService;
  showRegionField?: boolean;
  regions?: RegionWithRequiredFields[];
  isRegionFieldOptional?: boolean;
  isSingleFieldAddressLookup?: boolean;
};

export const StandardAddressForm: FC<StandardAddressFormProps> = ({
  fieldName = (name) => name,
  enableStreetNameAutocomplete,
  orderDeliveryLimits,
  postCodeConfig,
  locationServiceAutocompleteProvider,
  showRegionField,
  regions,
  isRegionFieldOptional,
  isSingleFieldAddressLookup,
}) => {
  const { __ } = useTranslations();
  const form = useFormContext();
  const isCityLimitationEnabled = orderDeliveryLimits?.mode === "yes_city";
  const deliveryLimitCitiesDropdownList = getDeliveryLimitCitiesDropdownListV2(orderDeliveryLimits?.limits);

  const regionDropdownOptions = useMemo(() => {
    return getRegionOptions(regions);
  }, [regions]);

  const handleOnUpdateStreet = () => {
    form.trigger();
  };

  return (
    <div className={styles.container}>
      <StreetAddressFields
        enableStreetNameAutocomplete={enableStreetNameAutocomplete}
        fieldName={fieldName}
        onUpdateStreet={handleOnUpdateStreet}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
        isSingleFieldAddressLookup={isSingleFieldAddressLookup}
      />
      <div className={styles.addressFields}>
        {!postCodeConfig?.shouldHidePostCode && (
          <div className={styles.postcode}>
            <PostCode fieldName={fieldName} config={postCodeConfig} />
          </div>
        )}
        <div className={styles.city}>
          <CityField
            fieldName={fieldName}
            isCityLimitationEnabled={isCityLimitationEnabled}
            deliveryLimitCitiesDropdownList={deliveryLimitCitiesDropdownList}
            orderDeliveryLimits={orderDeliveryLimits}
          />
        </div>
        {showRegionField && (
          <div className={styles.regionCode}>
            <RegionField fieldName={fieldName} regions={regionDropdownOptions} required={!isRegionFieldOptional} />
          </div>
        )}
        <div className={styles.country}>
          <CountryField fieldName={fieldName} />
        </div>
      </div>
    </div>
  );
};
